// Lien rights notice — pre-written from the invoice's lien dates, editable before it goes out.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getLienReminderDraft, sendLienReminder } from "@/lib/api";
import { shortDate, money } from "@/lib/format";

const errMsg = (e) => e?.response?.data?.detail || e?.message || "Failed";

export function LienReminderDialog({ row, onClose, onSent }) {
  const [draft, setDraft] = useState(null);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getLienReminderDraft(row.id)
      .then(({ data }) => { setDraft(data); setTo(data.to || ""); setSubject(data.subject || ""); setBody(data.body || ""); })
      .catch((e) => { toast.error(errMsg(e)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);

  const send = async () => {
    if (!to.trim()) { toast.error("Add a recipient email"); return; }
    setBusy(true);
    try {
      const { data } = await sendLienReminder(row.id, { recipient_email: to.trim(), subject, body });
      if (data.status === "sent") toast.success(`Lien rights notice sent to ${data.to}`);
      else toast.warning(`Notice logged but not delivered (${data.error || data.status}) — check Email Settings`);
      onSent?.();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="dialog-lien-reminder">
        <DialogHeader>
          <DialogTitle>Lien rights notice — invoice {row.invoice_number}</DialogTitle>
          <DialogDescription>
            {row.customer_name} · balance {money(row.balance)}. Sent through the reminder email path and logged on the invoice's notes.
          </DialogDescription>
        </DialogHeader>
        {!draft ? (
          <div className="py-6 text-center text-sm text-slate-400" data-testid="lien-reminder-loading">Preparing the notice…</div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-x-4 gap-y-1 rounded border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900" data-testid="lien-reminder-dates">
              <span>Pay by <b className="num">{shortDate(draft.pay_by)}</b></span>
              <span>Intent due <b className="num">{shortDate(draft.intent_due)}</b></span>
              <span>Lien <b className="num">{shortDate(draft.lien_date)}</b></span>
              {draft.last_sent && <span className="text-amber-700">Last sent {shortDate(draft.last_sent)}</span>}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="billing@customer.com" data-testid="lien-reminder-to" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="lien-reminder-subject" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Message</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={11} className="text-sm" data-testid="lien-reminder-body" />
              <div className="text-[11px] text-slate-500">[Pay Now] becomes the invoice link button.</div>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="lien-reminder-cancel">Cancel</Button>
          <Button onClick={send} disabled={busy || !draft || !draft.ready} data-testid="lien-reminder-send">
            {busy ? "Sending…" : "Send notice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
